import { Injectable } from '@angular/core';

/**
 * Service responsible for managing volume and mute state with localStorage persistence.
 * Extracted from VideoPlayerComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class VolumeService {
  private readonly VOLUME_STORAGE_KEY = 'drawfiend-volume';
  private readonly MUTE_STORAGE_KEY = 'drawfiend-muted';
  readonly DEFAULT_VOLUME = 0.8; // Default to 80% instead of 100%

  /**
   * Save volume to localStorage (only if persistence is enabled)
   */
  saveVolume(volume: number, persistenceEnabled: boolean): void {
    if (!persistenceEnabled) {
      console.log('Volume persistence disabled, not saving volume');
      return;
    }

    try {
      localStorage.setItem(this.VOLUME_STORAGE_KEY, volume.toString());
    } catch (e) {
      console.warn('Failed to save volume to localStorage:', e);
    }
  }

  /**
   * Load volume from localStorage (only if persistence is enabled)
   */
  loadVolume(persistenceEnabled: boolean): number {
    if (!persistenceEnabled) {
      console.log('Volume persistence disabled, using default volume');
      return this.DEFAULT_VOLUME;
    }

    try {
      const saved = localStorage.getItem(this.VOLUME_STORAGE_KEY);
      if (saved !== null) {
        const volume = parseFloat(saved);
        return isNaN(volume) ? this.DEFAULT_VOLUME : Math.max(0, Math.min(1, volume));
      }
    } catch (e) {
      console.warn('Failed to load volume from localStorage:', e);
    }
    return this.DEFAULT_VOLUME;
  }

  /**
   * Save mute state to localStorage (only if persistence is enabled)
   */
  saveMuteState(muted: boolean, persistenceEnabled: boolean): void {
    if (!persistenceEnabled) {
      console.log('Volume persistence disabled, not saving mute state');
      return;
    }

    try {
      localStorage.setItem(this.MUTE_STORAGE_KEY, muted.toString());
    } catch (e) {
      console.warn('Failed to save mute state to localStorage:', e);
    }
  }

  /**
   * Load mute state from localStorage (only if persistence is enabled)
   */
  loadMuteState(persistenceEnabled: boolean): boolean {
    if (!persistenceEnabled) {
      console.log('Volume persistence disabled, using default mute state (false)');
      return false;
    }

    try {
      const saved = localStorage.getItem(this.MUTE_STORAGE_KEY);
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (e) {
      console.warn('Failed to load mute state from localStorage:', e);
    }
    // Default to unmuted for new users or when no setting is saved
    return false;
  }

  /**
   * Check if this is a first-time user (no volume settings saved) or if persistence is disabled
   */
  isFirstTimeUser(persistenceEnabled: boolean): boolean {
    if (!persistenceEnabled) {
      return true;
    }

    try {
      const volumeSaved = localStorage.getItem(this.VOLUME_STORAGE_KEY);
      const muteSaved = localStorage.getItem(this.MUTE_STORAGE_KEY);
      return volumeSaved === null && muteSaved === null;
    } catch {
      return true; // Assume first-time if we can't check localStorage
    }
  }

  /**
   * Apply saved volume and mute settings to a video element
   */
  applySavedVolumeSettings(video: HTMLVideoElement, persistenceEnabled: boolean): void {
    const isFirstTime = this.isFirstTimeUser(persistenceEnabled);
    const savedVolume = this.loadVolume(persistenceEnabled);
    const savedMuted = this.loadMuteState(persistenceEnabled);

    video.volume = savedVolume;
    video.muted = savedMuted;

    if (isFirstTime) {
      console.log(`First-time user: Setting default volume to ${Math.round(savedVolume * 100)}%, unmuted`);
    } else {
      console.log(`Applied saved volume: ${Math.round(savedVolume * 100)}%, muted: ${savedMuted}`);
    }
  }
}
